// src/tabs/logements/AlentoursAdminCard.jsx — PR-FIX-4c (ADR-161)
//
// Card POI admin draggable pour `AlentoursAdminEditor`. Affiche photo thumb +
// meta (nom, adresse, distance, rating) + badge état (AFFICHÉ/MASQUÉ/ÉPINGLÉ/
// EXCLU/MANUEL) + boutons d'action (Afficher / Épingler / Masquer / Supprimer).
//
// Drag-drop : useSortable hook @dnd-kit (pattern ADR-114 LivretModuleCard) +
// PointerSensor distance:8 + KeyboardSensor sortableKeyboardCoordinates (côté
// parent `AlentoursAdminEditor`).
//
// Modèle UX 2 axes :
//   - Axe 1 "Affiché au voyageur" : oui/non (toggle `displayed_to_guest`).
//     Disabled si manual_pinned ou manual_excluded (dérive force la valeur).
//   - Axe 2 "Mis en avant" : oui/non (toggle `manual_pinned`).
//     Tooltip : "Toujours en premier voyageur, jamais retiré par IA".
//
// Source discriminant (PR-FIX-4a) :
//   - google → match `place_id` stable.
//   - tisseo → match `tisseo_url` stable.
//   - manual → match `manual_id` UUID v4 (généré serveur).
//
// Bouton "Supprimer" visible UNIQUEMENT si source === 'manual' (call backend
// `livret_alentours_poi_delete_manual` soft delete). Pour google/tisseo, l'admin
// utilise `manual_excluded` via le bouton "Masquer".

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DS } from "../../lib/ui";

// Identifiant logique selon source (utilisé par appels API + dnd-kit useSortable id).
export function getPoiIdentifier(poi) {
  if (!poi || typeof poi !== "object") return null;
  if (poi.source === "google") return poi.place_id || null;
  if (poi.source === "tisseo") return poi.tisseo_url || null;
  if (poi.source === "manual") return poi.manual_id || null;
  // Heuristique legacy (POIs pré-PR-FIX-4a sans champ source).
  if (poi.place_id) return poi.place_id;
  if (poi.tisseo_url) return poi.tisseo_url;
  return null;
}

// Photo thumb 48x48 (priorité photo_custom_url admin > photo_reference Google).
function _photoSrc(poi, maxwidth = 96) {
  if (!poi) return null;
  if (typeof poi.photo_custom_url === "string" && poi.photo_custom_url) {
    return poi.photo_custom_url;
  }
  const ref = poi.photo_reference;
  if (!ref || typeof ref !== "string" || !/^[A-Za-z0-9_-]{10,500}$/.test(ref)) {
    return null;
  }
  return `/api/beds24-mapping?action=livret_photo_proxy&photo_reference=${encodeURIComponent(ref)}&maxwidth=${maxwidth}`;
}

// Calcule un badge d'état unique pour le POI (priorité : pinned > excluded > manuel > masqué > affiché).
function _computeBadge(poi) {
  if (!poi) return null;
  if (poi.manual_pinned === true) {
    return { label: "ÉPINGLÉ", emoji: "⭐", bg: DS.navy, color: DS.white };
  }
  if (poi.manual_excluded === true) {
    return { label: "EXCLU", emoji: "🚫", bg: "#e5e7eb", color: "#666" };
  }
  if (poi.source === "manual" || poi.manual_added === true) {
    return { label: "MANUEL", emoji: "✏️", bg: DS.green, color: DS.white };
  }
  if (poi.displayed_to_guest === false) {
    return { label: "MASQUÉ", emoji: "○", bg: "#e5e7eb", color: "#666" };
  }
  return { label: "AFFICHÉ", emoji: "✓", bg: DS.skyDeep, color: DS.white };
}

export default function AlentoursAdminCard({
  poi,
  onToggleDisplay,
  onTogglePin,
  onToggleExclude,
  onDelete,
  busy = false,
}) {
  const identifier = getPoiIdentifier(poi);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: identifier || "missing-id" });

  if (!poi) return null;

  const badge = _computeBadge(poi);
  const photoUrl = _photoSrc(poi);
  const isManual = poi.source === "manual" || poi.manual_added === true;
  const isPinned = poi.manual_pinned === true;
  const isExcluded = poi.manual_excluded === true;
  const isDisplayed = poi.displayed_to_guest !== false;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: DS.white,
    border: `1px solid ${isDragging ? DS.skyDeep : DS.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 10,
    boxShadow: isDragging
      ? "0 8px 20px rgba(33,83,135,0.25)"
      : "0 1px 3px rgba(0,0,0,0.05)",
    opacity: isDragging ? 0.85 : busy ? 0.6 : 1,
    fontFamily: DS.fontBody,
  };

  const displayName = (typeof poi.name === "string" && poi.name) || "(sans nom)";

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Handle drag — touchAction:none + aria-label name (ADR-114 a11y WCAG 2.1 AA) */}
      <span
        {...listeners}
        role="button"
        tabIndex={0}
        title="Glisser pour réordonner"
        aria-label={`Réordonner ${displayName}`}
        style={{
          cursor: "grab",
          color: "#aaa",
          fontSize: 18,
          padding: "4px 4px",
          userSelect: "none",
          touchAction: "none",
          minWidth: 24,
          minHeight: 32,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        ⋮⋮
      </span>

      {/* Photo thumb 48x48 (placeholder gradient si pas de photo) */}
      <div
        style={{
          width: 48,
          height: 48,
          minWidth: 48,
          borderRadius: 8,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${DS.navy} 0%, ${DS.sky} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: DS.white,
          fontSize: 18,
        }}
        aria-hidden="true"
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span>📍</span>
        )}
      </div>

      {/* Meta colonne */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            color: DS.navy,
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: DS.fontBody,
          }}
          title={displayName}
        >
          {displayName}
        </div>
        {(typeof poi.address === "string" && poi.address) ||
        typeof poi.distance_m === "number" ? (
          <div
            style={{
              fontSize: 11,
              color: "#666",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {poi.address ? poi.address : null}
            {poi.address && typeof poi.distance_m === "number" ? " · " : null}
            {typeof poi.distance_m === "number" && poi.distance_m > 0
              ? poi.distance_m < 1000
                ? `${poi.distance_m} m`
                : `${(poi.distance_m / 1000).toFixed(1)} km`
              : null}
          </div>
        ) : null}
        {typeof poi.rating === "number" && poi.rating > 0 ? (
          <div style={{ fontSize: 11, color: DS.skyDeep, marginTop: 2 }}>
            ⭐ {poi.rating}
            {typeof poi.user_ratings_total === "number" &&
              poi.user_ratings_total > 0 && (
                <span style={{ color: "#888" }}> ({poi.user_ratings_total})</span>
              )}
          </div>
        ) : null}
      </div>

      {/* Badge état */}
      {badge && (
        <span
          aria-label={`État : ${badge.label}`}
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.3,
            padding: "4px 8px",
            borderRadius: 999,
            background: badge.bg,
            color: badge.color,
            whiteSpace: "nowrap",
            fontFamily: DS.fontBody,
          }}
        >
          {badge.emoji} {badge.label}
        </span>
      )}

      {/* Actions — minHeight 32 (compact admin grid) */}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {/* Afficher (toggle displayed_to_guest, disabled si pin/exclu) */}
        <button
          type="button"
          onClick={() => onToggleDisplay?.(poi, !isDisplayed)}
          disabled={busy || isPinned || isExcluded}
          title={
            isPinned
              ? "Désactivé : le POI est épinglé (toujours affiché)"
              : isExcluded
                ? "Désactivé : le POI est exclu (toujours masqué)"
                : isDisplayed
                  ? "Masquer du voyageur"
                  : "Afficher au voyageur"
          }
          aria-label={
            isDisplayed
              ? `Masquer ${displayName} du voyageur`
              : `Afficher ${displayName} au voyageur`
          }
          style={_btnStyle(isDisplayed ? DS.skyDeep : "#666", busy || isPinned || isExcluded)}
        >
          {isDisplayed ? "👁️" : "🚫"}
        </button>

        {/* Épingler (toggle manual_pinned, P6 enrich photo auto si needed) */}
        <button
          type="button"
          onClick={() => onTogglePin?.(poi, !isPinned)}
          disabled={busy}
          title="Toujours en premier voyageur, jamais retiré par IA"
          aria-label={
            isPinned ? `Désépingler ${displayName}` : `Épingler ${displayName}`
          }
          aria-pressed={isPinned}
          style={_btnStyle(isPinned ? DS.navy : "#666", busy)}
        >
          ⭐
        </button>

        {/* Masquer/Exclure (toggle manual_excluded) */}
        <button
          type="button"
          onClick={() => onToggleExclude?.(poi, !isExcluded)}
          disabled={busy}
          title="Exclu définitivement, jamais rétabli par IA"
          aria-label={
            isExcluded
              ? `Réactiver ${displayName}`
              : `Exclure ${displayName} définitivement`
          }
          aria-pressed={isExcluded}
          style={_btnStyle(isExcluded ? DS.charge : "#666", busy)}
        >
          🚷
        </button>

        {/* Supprimer (uniquement source=manual) */}
        {isManual && (
          <button
            type="button"
            onClick={() => onDelete?.(poi)}
            disabled={busy}
            title="Supprimer ce POI manuel"
            aria-label={`Supprimer ${displayName} (POI manuel)`}
            style={_btnStyle(DS.charge, busy)}
          >
            🗑️
          </button>
        )}
      </div>
    </div>
  );
}

function _btnStyle(color, disabled) {
  return {
    background: "transparent",
    border: `1px solid ${DS.border}`,
    borderRadius: 6,
    padding: "6px 8px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 14,
    color,
    minWidth: 32,
    minHeight: 32,
    opacity: disabled ? 0.5 : 1,
    lineHeight: 1,
  };
}
